import React, { useState, useEffect } from "react";
import { Table, Checkbox } from "antd";
import dayjs from "dayjs";

const SuffixInvList = ({ billingData, handleSuffixSelectionChange }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const dataSource = billingData?.suffixInventories?.map((inv, index) => ({
    key: index,
    ...inv,
  }));

  // Initialize selection on component mount
  useEffect(() => {
    if (billingData?.suffixInventories) {
      const initialKeys = billingData.suffixInventories
        .map((inv, index) => ({ inv, index }))
        .filter(({ inv }) => !isAllDatesBlocked(inv, billingData))
        .map(({ index }) => index);

      setSelectedRowKeys(initialKeys);

      handleSuffixSelectionChange(
        billingData.suffixInventories.filter((_, index) =>
          initialKeys.includes(index)
        )
      );
    }
  }, [billingData?.suffixInventories]);

  const isAllDatesBlocked = (record, billingData) => {
    const disabledFn = getDisabledDates(record, billingData);

    const bookingStart = record.startDate ? dayjs(record.startDate) : null;
    const bookingEnd = record.endDate ? dayjs(record.endDate) : null;

    if (!bookingStart || !bookingEnd) return true;

    let allBlocked = true;
    let currentDate = bookingStart;

    while (
      currentDate.isBefore(bookingEnd) ||
      currentDate.isSame(bookingEnd, "day")
    ) {
      if (!disabledFn(currentDate)) {
        allBlocked = false;
        break;
      }
      currentDate = currentDate.add(1, "day");
    }

    return allBlocked;
  };

  const getDisabledDates = (record, billingData) => {
    const disabledRanges = [];

    if (billingData?.billedSuffixInvs) {
      const billedInv = billingData.billedSuffixInvs.find(
        (inv) => inv.invId === record._id
      );

      if (billedInv?.billedPeriod) {
        billedInv.billedPeriod.forEach(({ startDate, endDate }) => {
          const start = dayjs(startDate).startOf("day");
          const end = dayjs(endDate).endOf("day");
          disabledRanges.push({ start, end });
        });
      }
    }

    return (current) => {
      if (!current) return false;

      const currentDate = dayjs(current).startOf("day");
      const bookingStart = record.startDate
        ? dayjs(record.startDate).startOf("day")
        : null;
      const bookingEnd = record.endDate
        ? dayjs(record.endDate).endOf("day")
        : null;

      if (
        bookingStart &&
        bookingEnd &&
        (currentDate.isBefore(bookingStart) || currentDate.isAfter(bookingEnd))
      ) {
        return true;
      }

      return disabledRanges.some(
        ({ start, end }) =>
          currentDate.isSame(start, "day") ||
          currentDate.isSame(end, "day") ||
          currentDate.isBetween(start, end, "day", "[]")
      );
    };
  };

  const handleSelect = (key) => {
    const inventory = billingData.suffixInventories[key];

    // If the inventory is blocked, don't allow selection
    if (isAllDatesBlocked(inventory, billingData)) {
      return;
    }

    const updatedKeys = selectedRowKeys.includes(key)
      ? selectedRowKeys.filter((item) => item !== key)
      : [...selectedRowKeys, key];

    setSelectedRowKeys(updatedKeys);

    handleSuffixSelectionChange(
      billingData.suffixInventories.filter((_, index) =>
        updatedKeys.includes(index)
      )
    );
  };

  const columns = [
    {
      title: "Select",
      dataIndex: "select",
      key: "select",
      align: "center",
      render: (_, record) => {
        const allBlocked = isAllDatesBlocked(record, billingData);
        return (
          <Checkbox
            checked={selectedRowKeys.includes(record.key)}
            onChange={() => handleSelect(record.key)}
            disabled={allBlocked}
          />
        );
      },
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      align: "center",
    },
    {
      title: "Suffix Code",
      dataIndex: "suffixCode",
      key: "suffixCode",
      align: "center",
    },
    {
      title: "Suffix Info",
      dataIndex: "suffixInfo",
      key: "suffixInfo",
      align: "center",
    },
    {
      title: "Purchase Rate",
      dataIndex: "purchaseRate",
      key: "purchaseRate",
      align: "center",
    },
    {
      title: "Billing Amount",
      dataIndex: "specialRate",
      key: "specialRate",
      align: "center",
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      align: "center",
      render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      align: "center",
      render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
    },
  ];

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
            textAlign: "center",
          }}
        />
      ),
    },
  };

  const rowClassName = (record) => {
    return isAllDatesBlocked(record, billingData) ? "disabled-row" : "";
  };

  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      components={components}
      scroll={{ x: "max-content" }}
      rowClassName={rowClassName}
    />
  );
};

export default SuffixInvList;
